import { useCallback, useEffect, useRef } from "react";
import { conversationPartToReasoningStep, historyApiToHistory, } from "../../api/codeer";
const isAbortError = (error) => typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AbortError";
const MAX_CHAT_NAME_LENGTH = 256;
const UNAUTHORIZED_ERROR_MESSAGE = "Unauthorized";
const truncateChatName = (name) => Array.from(name).slice(0, MAX_CHAT_NAME_LENGTH).join("");
const parseErrorCode = (value) => {
    if (typeof value === "number" && Number.isFinite(value))
        return value;
    if (typeof value !== "string")
        return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};
const createErrorWithCode = (message, code, serverMessage) => {
    const formattedMessage = code === undefined ? message : `[code=${code}] ${message}`;
    const error = new Error(formattedMessage);
    error.code = code;
    error.serverMessage = serverMessage;
    return error;
};
const buildJsonPayload = (payload, includeToolTags, contractVersion) => {
    const jsonPayload = {
        message: payload.message,
        stream: payload.stream,
        agent_id: payload.agentId,
        agent_history_id: payload.agentHistoryId,
        attached_file_uuids: payload.attachedFileUuids ?? [],
        resume_form_request_id: payload.resumeFormRequestId,
        mode: payload.mode,
        current_page_url: payload.currentPageUrl,
        viewing_workspace_id: payload.viewingWorkspaceId,
        user_locale: payload.userLocale,
    };
    if (contractVersion === "v1") {
        jsonPayload.include_tool_tags = includeToolTags;
    }
    else if (payload.imageEdit) {
        jsonPayload.image_edit = {
            base_attachment_id: payload.imageEdit.baseAttachmentId,
            region: payload.imageEdit.region,
        };
    }
    return JSON.stringify(jsonPayload);
};
const buildRegeneratePayload = (payload, includeToolTags, contractVersion) => {
    const jsonPayload = {
        conversation_group_id: payload.conversationGroupId,
        stream: payload.stream,
        mode: payload.mode,
        current_page_url: payload.currentPageUrl,
        viewing_workspace_id: payload.viewingWorkspaceId,
        user_locale: payload.userLocale,
    };
    if (contractVersion === "v1") {
        jsonPayload.include_tool_tags = includeToolTags;
    }
    return JSON.stringify(jsonPayload);
};
async function processSSEStream(response, historyId, callbacks) {
    const { onMessage, onReasoningStepStart, onReasoningStepEnd, onPartCreated, onPartCompleted, onChatTitleUpdated, onPendingInteractionResolved, onInteractionRequest, onHistoryAnalysisProgress, onCreated, onReceived, onDone, onError, } = callbacks;
    if (!response.body) {
        throw new Error("No response body");
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let eventName = null;
    let dataLines = [];
    let doneCalled = false;
    let hasReceivedText = false;
    const callDoneOnce = () => {
        if (onDone && !doneCalled) {
            onDone();
            doneCalled = true;
        }
    };
    const safeCall = (label, fn) => {
        try {
            fn();
        }
        catch (e) {
            console.error(`Error processing ${label}:`, e);
        }
    };
    const dispatchEvent = async () => {
        if (dataLines.length === 0 && !eventName)
            return false;
        const payload = dataLines.join("\n").trim();
        const rawEvent = eventName || "";
        if (!payload) {
            eventName = null;
            dataLines = [];
            return false;
        }
        if (payload === "[DONE]") {
            callDoneOnce();
            return true;
        }
        if (rawEvent.toLowerCase() === "error") {
            onError?.(new Error(payload || "Stream error"));
            return true;
        }
        let parsed = null;
        try {
            parsed = JSON.parse(payload);
        }
        catch (e) {
            console.warn("Failed to parse SSE payload", {
                event: rawEvent,
                payload,
                error: e instanceof Error ? e.message : String(e),
            });
            eventName = null;
            dataLines = [];
            throw e;
        }
        if (parsed && typeof parsed === "object") {
            const type = parsed.type || rawEvent;
            if (type === "response.part.created") {
                const part = parsed.part;
                if (part && typeof part === "object") {
                    safeCall(type, () => onPartCreated?.(part));
                }
                if (onReasoningStepStart &&
                    part &&
                    typeof part === "object" &&
                    part.part_kind === "tool-call") {
                    const step = conversationPartToReasoningStep(part);
                    if (step) {
                        safeCall(type, () => onReasoningStepStart(step));
                    }
                }
            }
            else if (type === "response.output_text.delta" ||
                type === "response.part.delta") {
                const delta = typeof parsed.delta === "string" ? parsed.delta : "";
                if (delta && onMessage) {
                    safeCall(type, () => {
                        onMessage(delta);
                        hasReceivedText = true;
                    });
                }
            }
            else if (type === "response.output_text.completed" ||
                type === "response.part.completed") {
                const part = parsed.part;
                if (part && typeof part === "object") {
                    safeCall(type, () => onPartCompleted?.(part));
                }
                if (part?.part_kind === "tool-return" && onReasoningStepEnd) {
                    const step = conversationPartToReasoningStep(part);
                    if (step) {
                        safeCall(type, () => onReasoningStepEnd(step));
                    }
                }
                const partFinalText = part?.part_kind === "text" &&
                    typeof part.content?.content === "string"
                    ? part.content.content
                    : "";
                const finalText = typeof parsed.final_text === "string"
                    ? parsed.final_text
                    : partFinalText;
                if (!hasReceivedText && finalText && onMessage) {
                    safeCall(type, () => {
                        onMessage(finalText);
                        hasReceivedText = true;
                    });
                }
                if (type === "response.output_text.completed") {
                    callDoneOnce();
                }
            }
            else if (type === "response.error" || type === "response.failed") {
                const message = typeof parsed.message === "string" ? parsed.message : "Stream error";
                onError?.(createErrorWithCode(message, parseErrorCode(parsed.code)));
                return true;
            }
            else if (type === "response.reasoning_step.start") {
                if (onReasoningStepStart && "step" in parsed) {
                    safeCall("reasoning_step.start", () => onReasoningStepStart(parsed.step));
                }
            }
            else if (type === "response.reasoning_step.end") {
                if (onReasoningStepEnd && "step" in parsed) {
                    safeCall("reasoning_step.end", () => onReasoningStepEnd(parsed.step));
                }
            }
            else if (type === "response.heartbeat") {
                if (onHistoryAnalysisProgress &&
                    typeof parsed.run_id === "string" &&
                    typeof parsed.workspace_id === "string" &&
                    typeof parsed.agent_id === "string" &&
                    typeof parsed.status === "string") {
                    safeCall("history_analysis.progress", () => onHistoryAnalysisProgress({
                        runId: parsed.run_id,
                        workspaceId: parsed.workspace_id,
                        agentId: parsed.agent_id,
                        status: parsed.status,
                        progress: parsed.progress && typeof parsed.progress === "object"
                            ? parsed.progress
                            : undefined,
                    }));
                }
            }
            else if (type === "response.chat.title.updated") {
                const name = typeof parsed.name === "string" ? parsed.name : "";
                if (onChatTitleUpdated && name) {
                    safeCall("chat.title.updated", () => {
                        const chatId = typeof parsed.chat_id === "number" ? parsed.chat_id : historyId;
                        onChatTitleUpdated(chatId, name);
                    });
                }
            }
            else if (type === "response.pending_interaction_resolved" ||
                type === "response.interaction.resolved") {
                if (onPendingInteractionResolved && Array.isArray(parsed.resolutions)) {
                    const resolutions = parsed.resolutions;
                    safeCall("pending_interaction_resolved", () => {
                        onPendingInteractionResolved(resolutions.map((item) => {
                            const resolution = item;
                            return {
                                interactionType: resolution.interaction_type === "payment"
                                    ? "payment"
                                    : "form",
                                action: typeof resolution.action === "string"
                                    ? resolution.action
                                    : "",
                                status: typeof resolution.status === "string"
                                    ? resolution.status
                                    : "",
                                conversationGroupId: typeof resolution.conversation_group_id === "string"
                                    ? resolution.conversation_group_id
                                    : "",
                                displayMessage: typeof resolution.display_message === "string"
                                    ? resolution.display_message
                                    : "",
                                formRequestId: typeof resolution.form_request_id === "string"
                                    ? resolution.form_request_id
                                    : null,
                                paymentRequestId: typeof resolution.payment_request_id === "string"
                                    ? resolution.payment_request_id
                                    : null,
                                resolved: Boolean(resolution.resolved),
                            };
                        }));
                    });
                }
            }
            else if (type === "response.created") {
                if (onCreated) {
                    safeCall("response.created", () => {
                        const createdHistoryId = typeof parsed.chat_id === "number" ? parsed.chat_id : historyId;
                        onCreated({
                            historyId: createdHistoryId,
                            conversationGroupId: typeof parsed.conversation_group_id === "string"
                                ? parsed.conversation_group_id
                                : undefined,
                            userConversationId: typeof parsed.user_conversation_id === "number"
                                ? parsed.user_conversation_id
                                : undefined,
                        });
                    });
                }
            }
            else if (type === "response.received") {
                if (onReceived) {
                    safeCall("response.received", () => {
                        onReceived({
                            historyId,
                            conversationGroupId: typeof parsed.conversation_group_id === "string"
                                ? parsed.conversation_group_id
                                : undefined,
                            userConversationId: typeof parsed.user_conversation_id === "number"
                                ? parsed.user_conversation_id
                                : undefined,
                            userMeta: parsed.user_meta &&
                                typeof parsed.user_meta === "object" &&
                                !Array.isArray(parsed.user_meta)
                                ? parsed.user_meta
                                : undefined,
                        });
                    });
                }
            }
            else if (type === "response.interaction_request" ||
                type === "response.interaction.created") {
                if (onInteractionRequest) {
                    safeCall("interaction_request", () => {
                        const interactionType = parsed.interaction_type === "payment" ? "payment" : "form";
                        if (interactionType === "payment") {
                            const payment = parsed.payment;
                            if (!payment?.payment_request_id || !payment.checkout_url) {
                                console.warn("Invalid payment interaction payload", parsed);
                                return;
                            }
                            onInteractionRequest({
                                chatId: typeof parsed.chat_id === "number"
                                    ? parsed.chat_id
                                    : historyId,
                                conversationGroupId: typeof parsed.conversation_group_id === "string"
                                    ? parsed.conversation_group_id
                                    : "",
                                interactionType: "payment",
                                payment: {
                                    paymentRequestId: payment.payment_request_id,
                                    checkoutUrl: payment.checkout_url,
                                    merchantOrderNo: payment.merchant_order_no || "",
                                    amountTwd: typeof payment.amount_twd === "number"
                                        ? payment.amount_twd
                                        : 0,
                                    currency: payment.currency || "TWD",
                                    status: payment.status || "pending",
                                    itemDesc: payment.item_desc,
                                },
                                timeoutAt: typeof parsed.timeout_at === "string"
                                    ? parsed.timeout_at
                                    : undefined,
                            });
                            return;
                        }
                        const formSchema = parsed.form_schema;
                        if (!formSchema?.id || !Array.isArray(formSchema.fields)) {
                            console.warn("Invalid form interaction payload", parsed);
                            return;
                        }
                        const formPayload = {
                            chatId: typeof parsed.chat_id === "number" ? parsed.chat_id : historyId,
                            conversationGroupId: typeof parsed.conversation_group_id === "string"
                                ? parsed.conversation_group_id
                                : "",
                            interactionType: "form",
                            formRequestId: typeof parsed.form_request_id === "string"
                                ? parsed.form_request_id
                                : "",
                            formSchema: {
                                id: formSchema.id,
                                title: formSchema.title,
                                description: formSchema.description,
                                fields: formSchema.fields.map((f) => ({
                                    name: f.name,
                                    type: f.type,
                                    question: f.question,
                                    label: f.label,
                                    required: f.required,
                                    placeholder: f.placeholder,
                                    options: f.options,
                                    helpText: f.helpText,
                                    descriptionImage: f.descriptionImage,
                                    defaultValue: f.defaultValue,
                                })),
                                ui: formSchema.ui,
                            },
                            timeoutAt: typeof parsed.timeout_at === "string"
                                ? parsed.timeout_at
                                : undefined,
                        };
                        onInteractionRequest(formPayload);
                    });
                }
            }
            else if (type === "response.completed") {
                callDoneOnce();
            }
        }
        else {
            const ev = rawEvent.toLowerCase();
            if (payload && payload.trim() === "[DONE]") {
                callDoneOnce();
                return true;
            }
            if (ev === "error")
                return true;
        }
        eventName = null;
        dataLines = [];
        return false;
    };
    while (true) {
        const { value, done } = await reader.read();
        if (done)
            break;
        buffer += decoder.decode(value, { stream: true });
        let pos = 0;
        while (true) {
            const nl = buffer.indexOf("\n", pos);
            if (nl === -1) {
                buffer = buffer.slice(pos);
                break;
            }
            let line = buffer.slice(pos, nl);
            pos = nl + 1;
            if (line.endsWith("\r"))
                line = line.slice(0, -1);
            if (line === "") {
                if (dataLines.length > 0 || eventName) {
                    const shouldStop = await dispatchEvent();
                    if (shouldStop) {
                        try {
                            await reader.cancel();
                        }
                        catch (e) {
                            console.debug("Error canceling reader:", e);
                        }
                        return;
                    }
                }
                continue;
            }
            if (line.startsWith(":"))
                continue;
            if (line.startsWith("event:")) {
                eventName = line.slice(6).trim();
                continue;
            }
            if (line.startsWith("data:")) {
                const dataContent = line.slice(5).replace(/^\s/, "");
                if (dataContent.trim() === "[DONE]") {
                    callDoneOnce();
                    try {
                        await reader.cancel();
                    }
                    catch (e) {
                        console.debug("Error canceling reader:", e);
                    }
                    return;
                }
                dataLines.push(dataContent);
                continue;
            }
            if (dataLines.length > 0) {
                dataLines.push(line);
            }
        }
    }
    if (dataLines.length > 0 || eventName) {
        const shouldStop = await dispatchEvent();
        if (shouldStop)
            return;
    }
    callDoneOnce();
}
const buildAuthHeaders = async (getAuthHeaders) => {
    const headers = await getAuthHeaders?.();
    return headers ?? {};
};
async function fetchAndProcessSSE(requestConfig, historyId, callbacks, getAuthHeaders, onUnauthorized) {
    const authHeaders = await buildAuthHeaders(getAuthHeaders);
    const response = await fetch(requestConfig.url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...authHeaders,
        },
        credentials: "include",
        body: requestConfig.body,
        signal: requestConfig.signal,
    });
    if (response.status === 401) {
        onUnauthorized?.();
        throw new Error(UNAUTHORIZED_ERROR_MESSAGE);
    }
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const serverMessage = typeof errorData.message === "string"
            ? errorData.message
            : typeof errorData.error === "string"
                ? errorData.error
                : undefined;
        throw createErrorWithCode(`API error: ${errorData.error || errorData.message || "Unknown error"} (${response.status})`, parseErrorCode(errorData.error_code), serverMessage);
    }
    await processSSEStream(response, historyId, callbacks);
}
export const useSSEClient = ({ getBaseUrl, getAuthHeaders, onUnauthorized, createChatPath = "/chats", includeToolTags = true, chatPathPrefix = "/chats", contractVersion = "v1", }) => {
    const abortControllerRef = useRef(null);
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
        };
    }, []);
    const createChat = useCallback(async (payload) => {
        const authHeaders = await buildAuthHeaders(getAuthHeaders);
        const response = await fetch(`${getBaseUrl()}${createChatPath}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...authHeaders,
            },
            credentials: "include",
            body: JSON.stringify({
                name: truncateChatName(payload.name),
                agent_id: payload.agentId ?? undefined,
                mode: payload.mode,
                current_page_url: payload.currentPageUrl,
                viewing_workspace_id: payload.viewingWorkspaceId,
                user_locale: payload.userLocale,
            }),
        });
        if (response.status === 401) {
            onUnauthorized?.();
            throw new Error(UNAUTHORIZED_ERROR_MESSAGE);
        }
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const serverMessage = typeof errorData.message === "string" ? errorData.message : undefined;
            throw createErrorWithCode(`API error: ${errorData.message || "Failed to create chat"} (${response.status})`, parseErrorCode(errorData.error_code), serverMessage);
        }
        const resp = (await response.json());
        if (!resp.data) {
            throw new Error("Failed to create chat");
        }
        return historyApiToHistory(resp.data);
    }, [createChatPath, getAuthHeaders, getBaseUrl, onUnauthorized]);
    /**
     * Send a message and receive streaming response via Server-Sent Events (SSE)
     */
    const sendMessage = useCallback(async (historyId, payload, callbacks) => {
        if (!isMountedRef.current)
            return;
        // Cancel any ongoing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        try {
            await fetchAndProcessSSE({
                url: `${getBaseUrl()}${chatPathPrefix}/${historyId}/messages`,
                body: buildJsonPayload(payload, includeToolTags, contractVersion),
                signal: abortController.signal,
            }, historyId, callbacks, getAuthHeaders, onUnauthorized);
        }
        catch (err) {
            if (isAbortError(err)) {
                console.debug("Request was aborted");
                return;
            }
            console.error("SSE Error:", err);
            if (!(err instanceof Error) ||
                err.message !== UNAUTHORIZED_ERROR_MESSAGE) {
                callbacks.onError?.(err instanceof Error ? err : new Error("Unknown error"));
            }
            throw err;
        }
        finally {
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }
        }
    }, [
        chatPathPrefix,
        contractVersion,
        getAuthHeaders,
        getBaseUrl,
        includeToolTags,
        onUnauthorized,
    ]);
    /**
     * Regenerate a conversation turn by reprocessing the original user message via SSE
     */
    const regenerateMessage = useCallback(async (historyId, payload, callbacks) => {
        if (!isMountedRef.current)
            return;
        // Cancel any ongoing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        try {
            await fetchAndProcessSSE({
                url: `${getBaseUrl()}${chatPathPrefix}/${historyId}/regenerate`,
                body: buildRegeneratePayload(payload, includeToolTags, contractVersion),
                signal: abortController.signal,
            }, historyId, callbacks, getAuthHeaders, onUnauthorized);
        }
        catch (err) {
            if (isAbortError(err)) {
                console.debug("Regenerate request was aborted");
                return;
            }
            console.error("Regenerate SSE Error:", err);
            if (!(err instanceof Error) ||
                err.message !== UNAUTHORIZED_ERROR_MESSAGE) {
                callbacks.onError?.(err instanceof Error ? err : new Error("Unknown error"));
            }
            throw err;
        }
        finally {
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
            }
        }
    }, [
        chatPathPrefix,
        contractVersion,
        getAuthHeaders,
        getBaseUrl,
        includeToolTags,
        onUnauthorized,
    ]);
    /**
     * Cancel the current ongoing request
     */
    const cancelRequest = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);
    return {
        createChat,
        sendMessage,
        regenerateMessage,
        cancelRequest,
    };
};
//# sourceMappingURL=useSSEClient.js.map